import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { findIndex, find, debounce } from 'lodash';
import { qbPathQuestions } from 'legacy-src/constants/paths';
import { typeSetOnLearnosityItem } from 'chameleon-src/utility/utils';
import BlueBookButton from 'chameleon/packages/blue-book-player/library/bluebook-button/BlueBookButton';

const PreviewNavigation = ({
  learnosityData,
  hasMorePages,
  currentPage,
  location,
  items,
  router,
  params,
  pathname,
  loadMoreFF,
  onItemChange,
  onUrlUpdate,
  onItemGoTo,
}) => {
  const { app } = learnosityData;
  const itemObject = app.items();
  const [learnosityItems, setLearnosityItems] = useState([]);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(-1);
  const [isNavDisabled, setIsNavDisabled] = useState(true);
  const hasMoreItemsLoaded = useRef(null);
  const [navState, setNavState] = useState({
    start: 0,
    end: 0,
  });
  const buttonsClassNames = 'h-[40px] w-[78px] !font-bold text-[14px]';

  useEffect(() => {
    const onItemLoad = () => {
      const clickedItemRef = getInitialItemRefFromRoute();
      const clickedItemIndex = getIndexByReference(clickedItemRef);
      setIsNavDisabled(false);
      if (
        !hasMoreItemsLoaded.current &&
        loadMoreFF &&
        clickedItemIndex >= 0 &&
        learnosityItems.length
      ) {
        preloadItems(learnosityItems, clickedItemIndex);
        hasMoreItemsLoaded.current = true;
      }
      typeSetOnLearnosityItem(app.getCurrentItem()?.reference);
    };

    app.once('item:load', onItemLoad);
  }, [learnosityItems]);

  useEffect(() => {
    const cleanItems = items.map((obj) => obj.learnosity_item_reference);
    setLearnosityItems(cleanItems);
  }, [items]);

  useEffect(() => {
    const clickedItemRef = getInitialItemRefFromRoute();
    const clickedItemIndex = getIndexByReference(clickedItemRef);
    setCurrentPreviewIndex(clickedItemIndex);
  }, [learnosityItems?.toString()]);

  useEffect(() => {
    if (currentPreviewIndex > -1) {
      const reference = learnosityItems[currentPreviewIndex];
      onItemChange(currentPreviewIndex + 1);
      updateRouteUrl(reference);
    }
  }, [currentPreviewIndex, learnosityItems]);

  const preloadItems = (refs, currentItemIndex) => {
    const start =
      currentItemIndex - loadMoreFF >= 0 ? currentItemIndex - loadMoreFF : 0;

    const end =
      currentItemIndex + loadMoreFF < refs.length
        ? currentItemIndex + loadMoreFF
        : refs.length;

    const toLoad = [
      ...refs.slice(start, currentItemIndex),
      ...refs.slice(currentItemIndex + 1, end + 1),
    ];

    if (toLoad.length > 0) {
      addItems(
        toLoad,
        setNavState(() => ({
          start,
          end,
        })),
      );
    }
  };

  const addItems = (itemRefs = [], afterLoad = () => {}) =>
    new Promise((resolve) => {
      const stopNavigation = (e) => {
        e.preventDefault();
        resolve();
      };
      app.once('item:beforeunload', stopNavigation);
      app.once('item:load', afterLoad);
      app.addItems({
        items: itemRefs,
        removePreviousItems: false,
      });
    });

  const gotoQuestion = debounce((itemRef, itemIndex, isNext) => {
    setIsNavDisabled(true);

    if (loadMoreFF) {
      const nextIndexToLoad =
        navState.end < learnosityItems.length &&
        navState.end - itemIndex < loadMoreFF &&
        navState.end + 1;

      const previousIndexToLoad =
        navState.start > 0 &&
        itemIndex - navState.start < loadMoreFF &&
        navState.start - 1;

      if (nextIndexToLoad && isNext) {
        const itemToLoad = learnosityItems[nextIndexToLoad];
        if (itemToLoad) {
          addItems([itemToLoad], () => {
            setNavState((state) => ({
              ...state,
              end: nextIndexToLoad,
            }));
            setIsNavDisabled(false);
          }).then(() => {
            itemObject.goto(itemRef).then(() => {
              typeSetOnLearnosityItem(itemRef);
              setCurrentPreviewIndex(itemIndex);
            });
            onItemGoTo(app.questions());
          });
        } else {
          itemObject.goto(itemRef).then(() => {
            typeSetOnLearnosityItem(itemRef);
            onItemGoTo(app.questions());
            setCurrentPreviewIndex(itemIndex);
            setIsNavDisabled(false);
          });
        }
      }

      if ((previousIndexToLoad || previousIndexToLoad === 0) && !isNext) {
        const itemToLoad = learnosityItems[previousIndexToLoad];
        if (itemToLoad) {
          addItems([itemToLoad], () => {
            setNavState((state) => ({
              ...state,
              start: previousIndexToLoad,
            }));
            setIsNavDisabled(false);
          }).then(() => {
            itemObject.goto(itemRef).then(() => {
              typeSetOnLearnosityItem(itemRef);
              setCurrentPreviewIndex(itemIndex);
            });
            onItemGoTo(app.questions());
          });
        } else {
          itemObject.goto(itemRef).then(() => {
            typeSetOnLearnosityItem(itemRef);
            onItemGoTo(app.questions());
            setCurrentPreviewIndex(itemIndex);
            setIsNavDisabled(false);
          });
        }
      }

      if (
        (isNext && !nextIndexToLoad) ||
        (!isNext && !(previousIndexToLoad || previousIndexToLoad === 0))
      ) {
        itemObject.goto(itemRef).then(() => {
          typeSetOnLearnosityItem(itemRef);
          onItemGoTo(app.questions());
          setCurrentPreviewIndex(itemIndex);
          setIsNavDisabled(false);
        });
      }
    } else if (!(itemRef in app.getItems())) {
      app.addItems({
        items: [itemRef],
        removePreviousItems: false,
      });
      addItems([itemRef], () => {
        setIsNavDisabled(false);
      }).then(() => {
        itemObject.goto(itemRef).then(() => {
          typeSetOnLearnosityItem(itemRef);
          setCurrentPreviewIndex(itemIndex);
        });
        onItemGoTo(app.questions());
      });
    } else {
      itemObject.goto(itemRef).then(() => {
        setIsNavDisabled(false);
        setCurrentPreviewIndex(itemIndex);
      });
    }
  }, 0);

  const onNextClick = () => {
    const learnosityItemsLength = learnosityItems.length;
    const tempIndex = currentPreviewIndex + 1;
    const { subject } = params;
    const { query } = location;
    const shouldGetNextPage =
      tempIndex >= learnosityItemsLength && hasMorePages;

    if (shouldGetNextPage) {
      const page = parseInt(query.page, 10);
      query.page = Number.isNaN(page) ? 1 : page + 1;

      const forcePreview = query['force-preview'];

      if (!forcePreview) {
        query['force-preview'] = true;
        query.item = 'first';
      } else if (forcePreview === 'null') {
        query['force-preview'] = true;
      }

      router.push({
        pathname: `/${subject}${pathname}`,
        query,
      });
    } else {
      const lRef = learnosityItems[tempIndex];
      gotoQuestion(lRef, tempIndex, true);
    }
  };
  const onPrevClick = () => {
    const { query } = location;
    const { subject } = params;
    const tempIndex = currentPreviewIndex - 1;

    const shouldGetPrevPage = tempIndex <= -1 && currentPage !== 0;

    if (shouldGetPrevPage) {
      if (query.page > 0) {
        query.page = parseInt(query.page, 10) - 1;
        const forcePreview = query['force-preview'];
        if (!forcePreview || forcePreview === 'null') {
          query['force-preview'] = true;
        }
        query.item = 'last';

        router.push({
          pathname: `/${subject}${pathname}`,
          query,
        });
      }
    } else {
      const lRef = learnosityItems[tempIndex];
      gotoQuestion(lRef, tempIndex);
    }
  };

  const getIndexByReference = (reference) =>
    findIndex(learnosityItems, (ref) => ref === reference);

  const getInitialItemRefFromRoute = () => {
    const itemId = parseInt(params.previewId, 10);
    const item = items.find((item) => parseInt(item.item_id, 10) === itemId);
    return item?.learnosity_item_reference;
  };

  const updateRouteUrl = (reference) => {
    const { query } = location;
    const { subject } = params;
    const currentItem = find(items, { learnosity_item_reference: reference });
    if (currentItem) {
      const pathUrl = pathname
        ? `/${subject}${pathname}${currentItem.item_id}`
        : `/${subject}/${qbPathQuestions}/preview/item/${currentItem.item_id}`;
      const queryString = Object.keys(query)
        .map((key) => `${key}=${query[key]}`)
        .join('&');
      const newUrl = `${FYM_FRONTEND_URL}${pathUrl}${queryString ? `?${queryString}` : ''}`;
      window.history.replaceState({}, '', newUrl);
      onUrlUpdate(currentItem.item_id, newUrl);
    }
  };

  const { query } = location;
  const isPrevButtonEnabled =
    !isNavDisabled && (query.page > 0 || currentPreviewIndex > 0);
  const isNextButtonEnabled =
    !isNavDisabled &&
    (hasMorePages || currentPreviewIndex + 1 < learnosityItems.length) &&
    currentPreviewIndex >= -1;

  return (
    <div
      className="flex justify-end grow basis-0 flex-row"
      data-test-id="navigation-container"
    >
      <BlueBookButton
        isPrimary
        className={buttonsClassNames}
        disabled={!isPrevButtonEnabled}
        onClick={onPrevClick}
        data-test-id="back-button"
        data-cy="back-button"
      >
        Back
      </BlueBookButton>
      <BlueBookButton
        isPrimary
        className={`${buttonsClassNames} ml-[12px]`}
        onClick={onNextClick}
        disabled={!isNextButtonEnabled}
        data-test-id="next-button"
        data-cy="next-button"
      >
        Next
      </BlueBookButton>
    </div>
  );
};

PreviewNavigation.propTypes = {
  learnosityData: PropTypes.object.isRequired,
  hasMorePages: PropTypes.bool.isRequired,
  items: PropTypes.array,
  currentPage: PropTypes.number,
  pathname: PropTypes.string,
  onItemChange: PropTypes.func.isRequired,
  onItemGoTo: PropTypes.func.isRequired,
};

PreviewNavigation.defaultProps = {
  currentPage: 0,
  items: [],
  pathname: '/question_bank/questions/',
};

export default PreviewNavigation;
